import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ScoreBar } from '@/components/ui/ScoreBar';
import type { CategoryScore } from '../completeness.api';

interface CategoryCardProps {
  category: CategoryScore;
  clientId: string;
  incompleteCount: number;
  completeCount?: number;
  totalCount?: number;
  productsPath?: string;
  buildProductUrl?: (filters: { categoryId: string }) => string;
}

const CATEGORY_ICONS: Record<string, string> = {
  label: 'label',
  photos: 'photo_camera',
  packaging: 'inventory_2',
  hazardous: 'warning',
  marketing: 'campaign',
  nutrition: 'restaurant',
  allergens: 'health_and_safety',
  logistics: 'local_shipping',
};

export function CategoryCard({ category, clientId, incompleteCount, completeCount, totalCount, productsPath, buildProductUrl }: CategoryCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isComplete = category.score >= 100;
  const icon = CATEGORY_ICONS[category.categoryId] ?? 'category';

  return (
    <button
      type="button"
      data-testid={`category-card-${category.categoryId}`}
      className="w-full rounded-xl p-4 text-left transition-all duration-200 hover:shadow-md"
      style={{
        backgroundColor: 'var(--background)',
        border: '1px solid var(--border-soft)',
        boxShadow: 'var(--card-shadow)',
        cursor: 'pointer',
      }}
      onClick={() =>
        navigate(buildProductUrl?.({ categoryId: category.categoryId }) ?? `${productsPath ?? `/data-quality/${clientId}/products`}?category=${category.categoryId}`)
      }
    >
      {isComplete ? (
        <div className="flex flex-col items-center gap-2 py-2 text-center">
          <span
            className="material-symbols-outlined"
            style={{ fontSize: '28px', color: 'var(--success-dark)' }}
            aria-hidden="true"
          >
            check_circle
          </span>
          <span
            className="text-xs font-medium"
            style={{ color: 'var(--success-dark)' }}
          >
            {t('dashboard.allComplete', { category: category.categoryName })}
          </span>
        </div>
      ) : (
        <>
          <div className="mb-3 flex items-center gap-2">
            <div
              className="flex h-8 w-8 items-center justify-center rounded-lg"
              style={{ backgroundColor: 'var(--primary-light)' }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: '18px', color: 'var(--primary)' }}
                aria-hidden="true"
              >
                {icon}
              </span>
            </div>
            <span
              className="text-[0.8125rem] font-medium truncate"
              style={{ color: 'var(--foreground)' }}
            >
              {category.categoryName}
            </span>
          </div>

          <ScoreBar score={category.score} variant="compact" />

          {completeCount !== undefined && totalCount !== undefined && (
            <p
              className="mt-1.5 text-xs"
              style={{ color: 'var(--foreground-muted)' }}
            >
              {t('dashboard.categoryComplete', { complete: completeCount, total: totalCount })}
            </p>
          )}

          <p
            className="mt-1 text-xs"
            style={{ color: 'var(--foreground-muted)' }}
          >
            {t('dashboard.incompleteCount', { count: incompleteCount })}
          </p>
        </>
      )}

      {/* Action slot for future extensions (fase 2c) */}
      <div className="category-card-action" />
    </button>
  );
}

export function CategoryCardSkeleton() {
  return (
    <div
      className="rounded-xl p-4"
      style={{
        backgroundColor: 'var(--background)',
        border: '1px solid var(--border-soft)',
        boxShadow: 'var(--card-shadow)',
      }}
    >
      <div className="mb-3 flex items-center gap-2">
        <div className="skeleton h-8 w-8 rounded-lg" aria-hidden="true" />
        <div className="skeleton h-3 w-24 rounded-lg" aria-hidden="true" />
      </div>
      <div className="skeleton mb-2 h-2 w-full rounded-full" aria-hidden="true" />
      <div className="skeleton h-3 w-20 rounded-lg" aria-hidden="true" />
    </div>
  );
}
